import { Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import type { Client } from "features/Clients/types/client.types";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";

interface Props {
  client: Client;
  clientId: number | string;
}

export function ClientInfoCard({ client, clientId }: Props) {
  return (
    <Card>
      <CardBody>
        <h6 className="text-uppercase text-muted mb-3">Client</h6>
        <ClientDetails client={client} clientId={clientId} />
      </CardBody>
    </Card>
  );
}

export function ClientDetails({ client, clientId }: Props) {
  const displayName = getClientDisplayName(client);
  const name =
    displayName !== "—" ? displayName : client.email || `Client #${clientId}`;
  const primaryContact =
    client.contacts?.find((c) => c.isPrimary) ?? client.contacts?.[0];

  return (
        <div className="vstack gap-2">
          <Link to={`/clients/${clientId}`} className="fw-semibold link">
            {name}
          </Link>

          {client.email ? (
            <div className="d-flex align-items-center gap-2 text-muted small">
              <i className="ri-mail-line flex-shrink-0" />
              <a href={`mailto:${client.email}`} className="text-muted">
                {client.email}
              </a>
            </div>
          ) : null}

          {client.phone ? (
            <div className="d-flex align-items-center gap-2 text-muted small">
              <i className="ri-phone-line flex-shrink-0" />
              <a href={`tel:${client.phone}`} className="text-muted">
                {client.phone}
              </a>
            </div>
          ) : null}

          {client.city || client.state ? (
            <div className="d-flex align-items-center gap-2 text-muted small">
              <i className="ri-map-pin-line flex-shrink-0" />
              <span>
                {[client.city, client.state].filter(Boolean).join(", ")}
              </span>
            </div>
          ) : null}

          {client.gstNumber ? (
            <div className="d-flex align-items-center gap-2 text-muted small">
              <i className="ri-file-text-line flex-shrink-0" />
              <span>GST: {client.gstNumber}</span>
            </div>
          ) : null}

          {primaryContact ? (
            <div className="border-top pt-2 mt-1">
              <p
                className="text-muted text-uppercase fw-semibold mb-1"
                style={{ fontSize: "0.7rem" }}
              >
                Primary contact
              </p>
              {primaryContact.firstName || primaryContact.lastName ? (
                <div className="small fw-medium">
                  {[primaryContact.firstName, primaryContact.lastName]
                    .filter(Boolean)
                    .join(" ")}
                </div>
              ) : null}
              {primaryContact.designation ? (
                <div className="text-muted small">
                  {primaryContact.designation}
                </div>
              ) : null}
              {primaryContact.phone ? (
                <div className="text-muted small">
                  <a
                    href={`tel:${primaryContact.phone}`}
                    className="text-muted"
                  >
                    {primaryContact.phone}
                  </a>
                </div>
              ) : null}
              {primaryContact.email ? (
                <div className="text-muted small">
                  <a
                    href={`mailto:${primaryContact.email}`}
                    className="text-muted"
                  >
                    {primaryContact.email}
                  </a>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
  );
}
